// The self-improvement screen's display language. Pure: it turns the typed definitions the loop writes
// into readable facts and sentences, and decides the words for a status or a verdict. No React, no math
// beyond counting — every number on the screen arrived decided from the API.
import type { CorrectionStatus, CorrectionView } from '../types/corrections'
import type { LedgerRow, Proposal, ResolutionLevel, Rule, RuleScope } from '../types/improve'

/** One line of a definition rendered for a person: "Domain — frostbank.com", never `{"domain":…}`. */
export interface Fact {
  label: string
  value: string
}

const FIELD_WORDS: Record<string, string> = {
  domain: 'Domain', companyId: 'Company', nameContains: 'Name contains', kind: 'Kind',
  childCompanyId: 'Subsidiary', parentCompanyId: 'Parent', personId: 'Person', role: 'Role',
  agingAfterDays: 'Ages after', missedAfterDays: 'Missed after', days: 'Grace',
  surface: 'Read by', when: 'When', verdict: 'Then', because: 'Because',
  stage: 'Stage', companyKind: 'Company kind', threat: 'Threat', name: 'Name', match: 'Matches',
  phrasing: 'Write', 'voice-edit': 'A draft you rewrote',
}

const label = (key: string) => FIELD_WORDS[key] ?? key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[-_]/g, ' ').replace(/^./, (c) => c.toUpperCase())

const scalar = (v: unknown): string => {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'boolean') return v ? 'yes' : 'no'
  if (Array.isArray(v)) return v.map(scalar).join(', ')
  if (typeof v === 'object') return Object.entries(v as Record<string, unknown>).map(([k, x]) => `${label(k).toLowerCase()} ${scalar(x)}`).join(', ')
  return String(v)
}

/** A typed definition (or a discussion's hypothesis) as facts a person reads, one level flattened. */
export function factsOf(definition: Record<string, unknown> | null | undefined): Fact[] {
  if (!definition) return []
  const out: Fact[] = []
  for (const [key, value] of Object.entries(definition)) {
    if (value === null || value === undefined || value === '') continue
    if (typeof value === 'object' && !Array.isArray(value)) {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        if (v === null || v === undefined || v === '') continue
        out.push({ label: `${label(key)} · ${label(k).toLowerCase()}`, value: scalar(v) })
      }
      continue
    }
    out.push({ label: label(key), value: scalar(value) })
  }
  return out
}

const DAYS = (v: unknown) => (typeof v === 'number' ? `${v} day${v === 1 ? '' : 's'}` : scalar(v))

/** What the rule DOES, in one sentence, for the kinds we know. Null where the facts speak for themselves. */
export function ruleSentence(kind: string, definition: Record<string, unknown>): string | null {
  const d = definition as Record<string, unknown>
  const match = (d.match ?? {}) as Record<string, unknown>
  switch (kind) {
    case 'company-kind': {
      const who = match.domain ?? match.companyId ?? match.nameContains
      return who ? `Any company matching ${scalar(who)} is a ${scalar(d.kind)}.` : null
    }
    case 'company-parent':
      return `${scalar(d.childCompanyId)} rolls up to ${scalar(d.parentCompanyId)}.`
    case 'person-company':
      return `${scalar(d.personId)} is at ${scalar(d.companyId)}${d.role ? ` as ${scalar(d.role)}` : ''}.`
    case 'firm-domain':
      return `${scalar(d.domain)} is the firm's own mail, not a counterparty.`
    case 'public-domain':
      return `${scalar(d.domain)} is a public mail host, so it never names a company.`
    case 'competitor':
      return `${scalar(d.name ?? d.domain)} is a rival, so meeting them at an account is a threat.`
    case 'lifecycle':
      return `A ${scalar(d.kind)} ages after ${DAYS(d.agingAfterDays)} quiet and is missed after ${DAYS(d.missedAfterDays)}.`
    case 'next-step-grace':
      return `A next step stays actionable for ${DAYS(d.days)} past its date.`
    case 'classification-cue':
      return `When ${scalar(d.when)}, ${scalar(d.surface)} should read it as ${scalar(d.verdict)}.`
    default:
      return null
  }
}

export const SCOPE_WORDS: Record<RuleScope, string> = {
  entity: 'this one thing',
  pattern: 'anything like it',
}

export const SCOPE_BLURB: Record<RuleScope, string> = {
  entity: 'A fact about one company or person. It generalises to nothing else, so it is allowed to name them.',
  pattern: 'It generalises, so it was checked for gaming: it may not name the entity that produced it.',
}

export const STATUS_WORDS: Record<CorrectionStatus, string> = {
  open: 'waiting for tonight',
  improved: 'improved',
  'needs-human': 'needs you',
  declined: 'declined',
}

/** The tone a status carries. `needs-human` must never read as a success (the owner's standard). */
export const STATUS_TONE: Record<CorrectionStatus, string> = {
  open: 'open', improved: 'ok', 'needs-human': 'warn', declined: 'quiet',
}

/** Where a correction came from, provenance-side: `correction:<id>` etc, split for a link. */
export function provenanceOf(provenance: string): { kind: string; id: string | null } {
  const at = provenance.indexOf(':')
  if (at < 0) return { kind: provenance, id: null }
  return { kind: provenance.slice(0, at), id: provenance.slice(at + 1) || null }
}

/** A rule the person can read at a glance: "Company kind · anything like it". */
export function ruleTitle(rule: Rule): string {
  return `${label(rule.kind)} · ${SCOPE_WORDS[rule.scope] ?? rule.scope}`
}

/** The verification stages in the order they ran, so a verdict is never shown out of sequence. */
export type StageState = 'passed' | 'failed' | 'skipped'
export interface Stage {
  key: 'schema' | 'anti-gaming' | 'deterministic' | 'judged' | 'applied'
  title: string
  state: StageState
  detail: string
}

/** The timeline behind one proposal: schema, anti-gaming, deterministic replay, judged samples, applied. */
export function stagesOf(p: Proposal): Stage[] {
  const out: Stage[] = []
  out.push({ key: 'schema', title: 'Typed', state: 'passed', detail: `Written as a ${label(p.kind).toLowerCase()} rule, scoped to ${SCOPE_WORDS[p.scope] ?? p.scope}.` })
  out.push(p.antiGaming
    ? { key: 'anti-gaming', title: 'Anti-gaming', state: p.antiGaming.passed ? 'passed' : 'failed', detail: p.antiGaming.passed ? 'It does not name the thing that produced it.' : p.antiGaming.violations.join(' ') }
    : { key: 'anti-gaming', title: 'Anti-gaming', state: 'skipped', detail: 'Not checked: an entity fact is allowed to name its entity.' })
  const passedReplays = p.deterministic.replays.filter((r) => r.passed).length
  out.push({
    key: 'deterministic', title: 'Deterministic replay',
    state: p.deterministic.passed ? 'passed' : 'failed',
    detail: p.deterministic.replays.length === 0
      ? (p.deterministic.reason ?? 'Nothing was replayed.')
      : `${passedReplays} of ${p.deterministic.replays.length} replays answer correctly${p.deterministic.passed ? '.' : `. ${p.deterministic.reason ?? ''}`.trimEnd()}`,
  })
  out.push(p.judged
    ? { key: 'judged', title: 'Judged replay', state: p.judged.passed ? 'passed' : 'failed', detail: `${p.judged.samples.length} samples, mean ${p.judged.mean.toFixed(2)}, variance ${p.judged.variance.toFixed(3)}. ${p.judged.rationale}` }
    : { key: 'judged', title: 'Judged replay', state: 'skipped', detail: 'Not needed: this decider is code, not a model.' })
  out.push({
    key: 'applied', title: 'Applied', state: p.applied ? 'passed' : 'skipped',
    detail: p.applied ? 'Active from tonight; what it matches was re-filed.' : 'Not applied — it is kept as a negative lesson.',
  })
  return out
}

/** The ledger filter, applied. Kept pure so the screen's filtering is provable without a browser. */
export function filterLedger(items: CorrectionView[], status: CorrectionStatus | 'all', kind: string | 'all'): CorrectionView[] {
  return items.filter((c) => (status === 'all' || c.status === status) && (kind === 'all' || c.entityKind === kind))
}

// ── The self-learning ledger: where each correction was addressed, in a rep's words ──

/** The level as a person reads it. The server sends the same words (levelWords, commit included); these are
 *  the fallback and the filter vocabulary. */
export const LEVEL_WORDS: Record<ResolutionLevel, string> = {
  open: 'not addressed yet',
  'entity-fix': 'only this record, no rule',
  'entity-rule': 'only this record',
  'pattern-rule': 'a rule for every case like it',
  code: 'fixed in the code',
  'needs-human': 'waiting on a person',
}

/** A band-aid must never read as a success, and a root fix must read as one. */
export const LEVEL_TONE: Record<ResolutionLevel, string> = {
  open: 'open', 'entity-fix': 'warn', 'entity-rule': 'warn', 'pattern-rule': 'ok', code: 'ok', 'needs-human': 'quiet',
}

export const isRoot = (level: string) => level === 'pattern-rule' || level === 'code'
export const isBandAid = (level: string) => level === 'entity-fix' || level === 'entity-rule'

/** open · band-aids only · addressed at the root · everything. */
export type LevelFilter = 'all' | 'open' | 'band-aid' | 'addressed'

export const LEVEL_FILTER_WORDS: Record<LevelFilter, string> = {
  all: 'everything', open: 'open', 'band-aid': 'band-aids still standing', addressed: 'addressed at the root',
}

export function matchesLevel(level: string, filter: LevelFilter): boolean {
  switch (filter) {
    case 'all': return true
    case 'open': return level === 'open' || level === 'needs-human'
    case 'band-aid': return isBandAid(level)
    case 'addressed': return isRoot(level)
  }
}

/** The ledger rows, filtered by level, status and kind at once. Pure. */
export function filterLedgerRows(rows: LedgerRow[], level: LevelFilter, status: CorrectionStatus | 'all', kind: string | 'all'): LedgerRow[] {
  return rows.filter((r) => matchesLevel(r.level, level) && (status === 'all' || r.correction.status === status) && (kind === 'all' || r.correction.entityKind === kind))
}

/** "re-filed 11 others" / "moved nothing else" — the impact in one phrase. */
export function impactWords(count: number): string {
  if (count === 0) return 'moved nothing else'
  return `re-filed ${count} other${count === 1 ? '' : 's'}`
}

/** One moved record as a line: "Michael Page · prospect → vendor". */
export function impactLine(line: { entityLabel?: string | null; entityId: string; from?: string | null; to?: string | null }): string {
  const who = line.entityLabel ?? line.entityId
  if (line.from && line.to) return `${who} · ${line.from} → ${line.to}`
  if (line.to) return `${who} · now ${line.to}`
  return who
}

/** Every kind present on the ledger, with its count, so the filter offers only what exists. */
export function kindCounts(items: CorrectionView[]): { kind: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const c of items) counts.set(c.entityKind, (counts.get(c.entityKind) ?? 0) + 1)
  return [...counts.entries()].map(([kind, count]) => ({ kind, count })).sort((a, b) => b.count - a.count || a.kind.localeCompare(b.kind))
}

export const kindLabel = (kind: string) => label(kind)
